// Job filters (PD-17093). The server applies every one of these in
// JobsService (joinType 'custom'), so the list, its tab counts and the
// customer's Jobs tab read the same rules.

import { JOB_STATUS_LABELS, JOB_STATUSES } from '@supply-mover-2-0/shared';
import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
} from './factories';
import type { FilterConfig, FilterRegistry } from './types';

const JOB_LIST_KEYS = ['job', 'client-jobs-embedded'];

const STATUS_OPTIONS = JOB_STATUSES.map((status) => ({
	id: status,
	label: JOB_STATUS_LABELS[status],
}));

function dateRange(label: string, dateField: string): FilterConfig {
	return {
		...createDateRangeFilter({
			label,
			objectTable: 'job',
			dateField,
			listKeys: JOB_LIST_KEYS,
		}),
		queryMapping: { objectTable: 'job', dateField, joinType: 'custom' },
	};
}

export const jobFilters: FilterRegistry = {
	jobKeywords: {
		...createKeywordFilter({
			label: 'Keywords',
			placeholder: 'Search job name, job number, customer, description...',
			objectTable: 'job',
			listKeys: JOB_LIST_KEYS,
		}),
		queryParamName: 'jobKeywords',
	},

	jobStatus: {
		type: 'multi-select',
		label: 'Status',
		placeholder: 'Select statuses...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'jobStatus',
		multiple: true,
		options: STATUS_OPTIONS,
		queryMapping: { objectTable: 'job', joinType: 'custom' },
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: JOB_LIST_KEYS,
	},

	jobClients: {
		...createEntityLookupFilter({
			label: 'Customer',
			placeholder: 'Search customers...',
			apiEndpoint: '/clients',
			objectTable: 'job',
			fieldName: 'fk_client__id',
			listKeys: ['job'],
			displayFields: {
				id: 'id',
				primary: 'name',
				secondary: 'externalId',
			},
			colorScheme: 'green',
		}),
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryMapping: { objectTable: 'job', joinType: 'custom' },
		queryParamName: 'jobClients',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	jobProjects: {
		type: 'multi-select',
		label: 'Project',
		placeholder: 'Search projects...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/projects/options',
		queryParamName: 'jobProjects',
		multiple: true,
		queryMapping: { objectTable: 'job', joinType: 'custom' },
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['job'],
	},

	// The job's salesperson. "My Jobs" locks this to the caller on the server.
	jobSalespeople: {
		type: 'multi-select',
		label: 'Salesperson',
		placeholder: 'Search salespeople...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'salesperson_id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users/salespeople',
		queryParamName: 'jobSalespeople',
		multiple: true,
		options: [
			{ id: 'ME', label: 'Me' },
			{ id: 'null', label: 'Unassigned' },
		],
		queryMapping: { objectTable: 'job', joinType: 'custom' },
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: JOB_LIST_KEYS,
	},

	jobEstimatedValue: {
		type: 'number-range',
		label: 'Estimated Value',
		placeholder: 'Any',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'jobEstimatedValue',
		multiple: false,
		queryMapping: { objectTable: 'job', joinType: 'custom' },
		format: 'single',
		valueExtractor: {
			property: 'data',
		},
		min: 0,
		step: 1000,
		listKeys: JOB_LIST_KEYS,
	},

	jobStartDate: {
		...dateRange('Start Date', 'startDate'),
		queryParamName: 'jobStartDate',
	},

	jobEndDate: {
		...dateRange('End Date', 'endDate'),
		queryParamName: 'jobEndDate',
	},

	jobLastOrderDate: {
		...dateRange('Last Order', 'lastOrderDate'),
		queryParamName: 'jobLastOrderDate',
	},
};
